import { useMemo, useRef, useState } from "react";
import { toast } from "react-hot-toast";
import { FiUploadCloud, FiDownload, FiTrash2 } from "react-icons/fi";
import { useValidateSchemeProductCodesMutation } from "../../../features/tenant/scheme/schemeApi";
import { readSpreadsheetRows, downloadCsvFile } from "../../../utils/spreadsheet";

const MAX_ROWS = 10000;
const PREVIEW_ROWS = 20;

/**
 * SKU-wise points for user types whose source field is "sku_points".
 * File format: `product_code` + one column per such user type (`<user_type>` or `<user_type>_points`).
 * A blank cell = no row = that user type earns nothing for that SKU.
 *
 * Props:
 *  - skuUserTypes: [{ key: "retailer", name: "Retailer" }]
 *  - selectedProducts / applyProducts: the scheme's product list; when it is empty the
 *    uploaded SKUs become the product list (scheme scoped to exactly these SKUs)
 *  - rows / setRows: [{ product_code, points: { retailer: 10 } }] (full replacement set)
 */
const SkuPointsPanel = ({ skuUserTypes, selectedProducts, applyProducts, rows, setRows }) => {
  const fileRef = useRef(null);
  const [validateCodes, { isLoading }] = useValidateSchemeProductCodesMutation();
  const [errors, setErrors] = useState([]);
  const tenantId = JSON.parse(sessionStorage.getItem("vendorData"))?.tenant_id;

  const counts = useMemo(() => {
    const perType = {};
    skuUserTypes.forEach((u) => (perType[u.key] = 0));
    rows.forEach((r) => skuUserTypes.forEach((u) => { if (r.points?.[u.key] !== undefined && r.points[u.key] !== "") perType[u.key] += 1; }));
    return perType;
  }, [rows, skuUserTypes]);

  const productsWithoutRow = useMemo(() => {
    const have = new Set(rows.map((r) => r.product_code));
    return selectedProducts.filter((p) => !have.has(p.product_code));
  }, [rows, selectedProducts]);

  const downloadTemplate = () => {
    const headers = ["product_code", ...skuUserTypes.map((u) => `${u.key}_points`)];
    const byCode = new Map(rows.map((r) => [r.product_code, r.points || {}]));
    const body = selectedProducts.length
      ? selectedProducts.map((p) => [p.product_code, ...skuUserTypes.map((u) => byCode.get(p.product_code)?.[u.key] ?? "")])
      : [["SKU-001", ...skuUserTypes.map(() => "10")]];
    downloadCsvFile("scheme_sku_points_template.csv", headers, body);
  };

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setErrors([]);
    try {
      const fileRows = await readSpreadsheetRows(file);
      if (fileRows.length === 0) return setErrors(["The file is empty"]);
      if (fileRows.length > MAX_ROWS) return setErrors([`Too many rows (max ${MAX_ROWS})`]);
      if (!("product_code" in fileRows[0])) return setErrors(['Missing required column "product_code"']);

      const present = skuUserTypes.filter((u) => u.key in fileRows[0] || `${u.key}_points` in fileRows[0]);
      if (present.length === 0) {
        return setErrors([`No points column found. Expected: ${skuUserTypes.map((u) => `${u.key}_points`).join(", ")}`]);
      }

      const errs = [];
      const seen = new Set();
      const parsed = [];
      fileRows.forEach((r, i) => {
        const code = String(r.product_code ?? "").trim();
        if (!code) return errs.push(`Row ${i + 2}: product_code is empty`);
        if (seen.has(code)) return errs.push(`Row ${i + 2}: duplicate product_code "${code}"`);
        seen.add(code);
        const points = {};
        present.forEach((u) => {
          const raw = r[u.key] ?? r[`${u.key}_points`] ?? "";
          if (raw === "" || raw === null || raw === undefined) return;
          const n = Number(raw);
          if (!Number.isFinite(n) || n < 0) return errs.push(`Row ${i + 2} (${code}): invalid points "${raw}" for ${u.name}`);
          points[u.key] = n;
        });
        parsed.push({ product_code: code, points });
      });

      const codes = parsed.map((r) => r.product_code);
      const res = await validateCodes({ token: sessionStorage.getItem("access_token"), tenant_id: tenantId, codes }).unwrap();
      const { valid, inactive, unknown } = res.body;
      if (unknown.length) errs.push(`Unknown product codes (${unknown.length}): ${unknown.slice(0, 15).join(", ")}${unknown.length > 15 ? " …" : ""}`);
      if (inactive.length) errs.push(`Inactive products (${inactive.length}): ${inactive.slice(0, 15).join(", ")}${inactive.length > 15 ? " …" : ""}`);

      if (selectedProducts.length > 0) {
        const inScheme = new Set(selectedProducts.map((p) => p.product_code));
        const outside = codes.filter((c) => !inScheme.has(c));
        if (outside.length) errs.push(`Not in the scheme's selected products (${outside.length}): ${outside.slice(0, 15).join(", ")}${outside.length > 15 ? " …" : ""}`);
      }

      if (errs.length) return setErrors(errs.slice(0, 50));

      if (selectedProducts.length === 0) {
        applyProducts(
          valid.map((p) => ({ product_id: p.product_id, product_code: p.product_code, name: p.name || "", category_id: p.category_id, category_name: p.category_name })),
        );
        toast.success(`${valid.length} SKU(s) set as the scheme's products`);
      }
      setRows(parsed);
      toast.success(`${parsed.length} SKU row(s) loaded`);
    } catch (err) {
      setErrors([err?.data?.message || "Could not read the file"]);
    }
  };

  const clearRows = () => {
    if (rows.length && !window.confirm("Remove all SKU-wise points from this scheme?")) return;
    setRows([]);
    setErrors([]);
  };

  return (
    <div className="mt-6 rounded-xl border border-indigo-200 bg-indigo-50/40 p-4">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h3 className="text-sm font-semibold text-gray-800">SKU-wise points</h3>
          <p className="text-xs text-gray-500 mt-0.5">
            For: {skuUserTypes.map((u) => u.name).join(", ")}. Points apply only to this scheme. A SKU without a value earns nothing for that user type.
            {selectedProducts.length === 0 && " No products selected yet — the uploaded SKUs will become this scheme's product list."}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <input ref={fileRef} type="file" accept=".csv,.xls,.xlsx" className="hidden" onChange={handleFile} />
          <button type="button" onClick={downloadTemplate} className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg border border-gray-300 bg-white text-xs text-gray-700 hover:bg-gray-50">
            <FiDownload /> Template
          </button>
          <button type="button" disabled={isLoading} onClick={() => fileRef.current?.click()} className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-blue-600 hover:bg-blue-700 text-white text-xs font-medium disabled:opacity-50">
            <FiUploadCloud /> {isLoading ? "Validating…" : "Upload CSV/XLSX"}
          </button>
          {rows.length > 0 && (
            <button type="button" onClick={clearRows} className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg border border-red-200 bg-white text-xs text-red-600 hover:bg-red-50">
              <FiTrash2 /> Clear
            </button>
          )}
        </div>
      </div>

      {errors.length > 0 && (
        <div className="mt-3 rounded-lg border border-red-200 bg-red-50 p-3 text-xs text-red-700 space-y-0.5">
          <div className="font-semibold">Upload rejected — fix the file and try again:</div>
          {errors.map((m, i) => <div key={i} className="break-words">• {m}</div>)}
        </div>
      )}

      {rows.length > 0 ? (
        <div className="mt-3">
          <div className="flex flex-wrap gap-x-5 gap-y-1 text-xs text-gray-600">
            <span><strong>{rows.length}</strong> SKU(s)</span>
            {skuUserTypes.map((u) => <span key={u.key}>{u.name}: <strong>{counts[u.key]}</strong> value(s)</span>)}
          </div>
          {productsWithoutRow.length > 0 && (
            <p className="mt-1 text-xs text-amber-700">
              {productsWithoutRow.length} selected product(s) have no SKU points row and will earn nothing for these user types: {productsWithoutRow.slice(0, 8).map((p) => p.product_code).join(", ")}{productsWithoutRow.length > 8 ? " …" : ""}
            </p>
          )}
          <div className="mt-2 overflow-x-auto rounded-lg border border-gray-200 bg-white">
            <table className="min-w-full text-xs">
              <thead className="bg-gray-50 text-gray-500">
                <tr>
                  <th className="text-left px-3 py-2 font-medium">Product code</th>
                  {skuUserTypes.map((u) => <th key={u.key} className="text-right px-3 py-2 font-medium">{u.name}</th>)}
                </tr>
              </thead>
              <tbody>
                {rows.slice(0, PREVIEW_ROWS).map((r) => (
                  <tr key={r.product_code} className="border-t border-gray-100">
                    <td className="px-3 py-1.5 font-mono">{r.product_code}</td>
                    {skuUserTypes.map((u) => <td key={u.key} className="px-3 py-1.5 text-right">{r.points?.[u.key] ?? <span className="text-gray-300">—</span>}</td>)}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {rows.length > PREVIEW_ROWS && <p className="text-[11px] text-gray-400 mt-1">Showing first {PREVIEW_ROWS} of {rows.length} rows.</p>}
        </div>
      ) : (
        <p className="mt-3 text-xs text-amber-700">No SKU points uploaded yet — required for the user types above.</p>
      )}
    </div>
  );
};

export default SkuPointsPanel;
